import { Image, Platform, Share } from 'react-native'

import PdfDownloadLoader from '@zet/common/components/pdfDownloadLoader'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import VideoBanner from '@zet/common/components/videoBanner'
import useSharePdf from '@zet/common/hooks/useSharePdf'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { SH1, SH3, Text } from '@zet/design-system/typography'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import { useWebShare } from '@zet/feature-brands/hooks/useWebShare'
import { i18n } from '@zet/localization'
import PDF from '@zet/svg/pdf'

const MarketingResourcesContent = ({
  brandId,
  navigate,
  isFixedHeight,
  isRecommendationFlow,
  setIsBottomSheetVisible,
}: {
  brandId: string | number
  navigate: any
  isFixedHeight?: boolean
  isRecommendationFlow?: boolean
  setIsBottomSheetVisible?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { WebShare, openWebShare } = useWebShare()
  const { share, imageShare, isLoading } = useSharePdf()
  const { data: content } = useBrandDetailsById(brandId)
  const brandName = content?.name || ''
  const brandCategory = content?.subCategory || ''

  const onShare = async (videoId?: string) => {
    try {
      if (Platform.OS === 'web') {
        await openWebShare({
          text: `https://www.youtube.com/watch?v=${videoId}`,
        })
      } else {
        await Share.share({
          message: `https://www.youtube.com/watch?v=${videoId}`,
        })
      }
    } catch (error) {
      console.warn(error)
    }
  }

  const handleImageShare = async (imageUrl?: string) => {
    if (Platform.OS === 'web') {
      await openWebShare({
        text: imageUrl || '',
      })
    } else {
      imageShare(imageUrl)
    }
  }

  const handlePdfShare = async (pdf: string) => {
    if (Platform.OS === 'web') {
      await openWebShare({
        text: pdf || '',
      })
    } else {
      share(pdf)
    }
  }

  const shareImage = (
    isVideo?: boolean,
    videoId?: string,
    imageUrl?: string
  ) => {
    if (isVideo) {
      onShare(videoId)
    } else {
      handleImageShare(imageUrl)
    }
  }
  const ShareButton = ({
    isVideo,
    videoId,
    imageUrl,
    eventName,
    eventPayload,
  }: {
    isVideo?: boolean
    videoId?: string
    imageUrl?: string
    eventName?: string
    eventPayload?: object
  }) => {
    return (
      <PressableWithIntent
        name="MarketingResource"
        className="border-primary-100 active:bg-primary-50 my-3 rounded border py-2"
        onPress={() => shareImage(isVideo, videoId, imageUrl)}
        eventName={eventName}
        eventPayload={eventPayload}
      >
        <XStack className="items-center justify-center">
          <Icon type="Share" color="#2075F0" />
          <Text className="text-primary-100 ml-2">
            {i18n.t('marketingResources.cta')}
          </Text>
        </XStack>
      </PressableWithIntent>
    )
  }
  if (isLoading) {
    return <PdfDownloadLoader />
  }

  return (
    <YStack style={isFixedHeight ? { marginBottom: 40 } : {}}>
      {content?.marketingResources?.pdfs?.map((value: any, index: number) => (
        <Stack
          className="border-grays-30 rounded border-b-8 bg-white px-3 pt-3"
          key={index}
        >
          <PressableWithIntent
            onPress={() => {
              setIsBottomSheetVisible && setIsBottomSheetVisible(false)
              if (Platform.OS === 'web') {
                window.open(value.pdf, '_blank')
              } else {
                navigate('BrandPdf', {
                  offerLetterUrl: value.pdf,
                })
              }
            }}
            className="active:bg-primary-50"
            eventName="click_action"
            eventPayload={{
              item_name: isRecommendationFlow
                ? 'marketing_resource_recommendation_flow'
                : 'brands_list_marketing_resource',
              brand_name: brandName,
              brand_category: brandCategory,
              resource_type: 'pdf',
            }}
          >
            <XStack className="bg-primary-10 items-center px-5 py-6">
              <PDF />
              <SH3 className="ml-4 mr-0 w-[70%]">{content?.name}</SH3>
            </XStack>
          </PressableWithIntent>
          <SH1 className="my-3">{value.title}</SH1>
          <Pressable
            className="border-primary-100 active:bg-primary-50 mb-3 rounded border py-2"
            onPress={() => handlePdfShare(value.pdf)}
            eventName="click_action"
            eventPayload={{
              item_name: isRecommendationFlow
                ? 'marketing_resource_share_pdf_recommendation_flow'
                : 'brands_list_marketing_resource_share_pdf',
              brand_name: brandName,
              brand_category: brandCategory,
              resource_type: 'pdf',
            }}
          >
            <XStack className="items-center justify-center">
              <Icon type="Share" color="#2075F0" />
              <Text className="text-primary-100 ml-2">
                {i18n.t('marketingResources.cta')}
              </Text>
            </XStack>
          </Pressable>
        </Stack>
      ))}
      {content?.marketingResources?.creatives?.map(
        (value: any, index: number) => (
          <Stack
            className="border-grays-30 rounded border-b-8 bg-white px-3 pt-3"
            key={index}
          >
            <Stack className="h-[170] w-full">
              <Image
                source={{
                  uri: value?.thumbnail,
                }}
                className={`${
                  Platform.OS !== 'web' && 'h-full w-full rounded'
                }`}
                resizeMode="contain"
                resizeMethod="auto"
                style={
                  Platform.OS === 'web' ? { height: 170, width: '100%' } : {}
                }
              />
            </Stack>
            <ShareButton
              imageUrl={value?.thumbnail}
              eventName="click_action"
              eventPayload={{
                item_name: isRecommendationFlow
                  ? 'marketing_resource_creatives_recommendation_flow'
                  : 'brands_list_marketing_resource_share_creatives',
                brand_name: brandName,
                brand_category: brandCategory,
                resource_type: 'creatives',
              }}
            />
          </Stack>
        )
      )}
      {content?.marketingResources?.videos?.map(
        (value: object, index: number) => (
          <Stack
            className="border-grays-30 rounded border-b-8 bg-white px-3 pt-3"
            key={index}
          >
            <Stack className="h-[170] w-full overflow-hidden rounded">
              <VideoBanner
                image={value?.thumbnail}
                videoId={value?.videoId}
                videoData={
                  content?.marketingResources?.videoContent?.[index] ?? null
                }
                eventName="click_action"
                eventPayload={{
                  item_name: isRecommendationFlow
                    ? 'marketing_resource_video_recommendation_flow'
                    : 'brands_list_marketing_resource_video',
                  brand_name: brandName,
                  brand_category: brandCategory,
                  resource_type: 'video',
                }}
              />
            </Stack>
            <ShareButton
              isVideo
              videoId={value?.videoId}
              eventName="click_action"
              eventPayload={{
                item_name: isRecommendationFlow
                  ? 'marketing_resource_share_recommendation_flow'
                  : 'brands_list_marketing_resource_share',
                brand_name: brandName,
                brand_category: brandCategory,
                resource_type: 'video',
              }}
            />
          </Stack>
        )
      )}
      <WebShare />
    </YStack>
  )
}

export default MarketingResourcesContent
